import React, { useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Branch health: red = customers waiting on a first contact, amber = leads
// with a manager but no salesperson, green = queue is being worked.
const healthDot = (branch) =>
  branch.awaitingContact > 0
    ? "bg-red-500"
    : branch.needsSales > 0
    ? "bg-amber-500"
    : "bg-emerald-500";

const ringTone = (branch) =>
  branch.awaitingContact > 0
    ? "ring-red-500"
    : branch.needsSales > 0
    ? "ring-amber-500"
    : "ring-emerald-500";

const MiniBadge = ({ value, label, tone }) => (
  <span
    className={`inline-flex items-center rounded-full px-1.5 py-px text-[10px] font-bold leading-tight tabular-nums ${tone}`}
  >
    {value} {label}
  </span>
);

const COLLAPSE_KEY = "webleads.overview.collapsed";

const LocationOverview = ({ branchStats, selectedBranch, onSelectBranch }) => {
  // Collapsed unless the user has opened it before — the lead list is what
  // people come here for, so the board starts out of the way.
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) !== "0"
  );

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      localStorage.setItem(COLLAPSE_KEY, prev ? "0" : "1");
      return !prev;
    });
  };

  // Surface the branches that need attention first.
  const ordered = useMemo(
    () =>
      [...branchStats].sort(
        (a, b) =>
          b.awaitingContact - a.awaitingContact ||
          b.needsSales - a.needsSales ||
          b.active - a.active ||
          a.name.localeCompare(b.name)
      ),
    [branchStats]
  );

  const needsAttention = ordered.filter(
    (b) => b.awaitingContact > 0 || b.needsSales > 0
  ).length;

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          className="-ml-1 inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-1 pr-2 text-base font-semibold tracking-tight transition-colors hover:text-[color:var(--app-accent)]"
        >
          <ExpandMoreIcon
            fontSize="small"
            className={`app-muted transition-transform ${collapsed ? "-rotate-90" : ""}`}
          />
          Locations
          {collapsed && needsAttention > 0 && (
            <span className="ml-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-300">
              {needsAttention} need attention
            </span>
          )}
        </button>
        {selectedBranch !== "All Locations" && (
          <button
            type="button"
            onClick={() => onSelectBranch("All Locations")}
            className="app-link ml-auto text-sm"
          >
            Show all locations
          </button>
        )}
      </div>

      {!collapsed && (
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {ordered.map((branch) => {
            const selected = selectedBranch === branch.name;
            return (
              <button
                type="button"
                key={branch.name}
                onClick={() =>
                  onSelectBranch(selected ? "All Locations" : branch.name)
                }
                aria-pressed={selected}
                title={
                  selected
                    ? "Click to clear the branch filter"
                    : `Show ${branch.name} leads`
                }
                className={`app-card rounded-xl border px-3 py-2.5 text-left transition hover:-translate-y-0.5 ${
                  selected ? `ring-2 ${ringTone(branch)}` : ""
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${healthDot(branch)}`}
                  />
                  <span className="truncate text-xs font-semibold">
                    {branch.name}
                  </span>
                  <span className="ml-auto text-lg font-bold leading-none tabular-nums">
                    {branch.active}
                  </span>
                </div>
                <div className="mt-1.5 flex min-h-[16px] flex-wrap items-center gap-1">
                  {branch.awaitingContact > 0 && (
                    <MiniBadge
                      value={branch.awaitingContact}
                      label="no contact"
                      tone="bg-red-500/15 text-red-700 dark:text-red-300"
                    />
                  )}
                  {branch.needsSales > 0 && (
                    <MiniBadge
                      value={branch.needsSales}
                      label="need sales"
                      tone="bg-amber-500/15 text-amber-700 dark:text-amber-300"
                    />
                  )}
                  {branch.completedThisMonth > 0 && (
                    <MiniBadge
                      value={branch.completedThisMonth}
                      label="done"
                      tone="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                    />
                  )}
                  {branch.awaitingContact === 0 &&
                    branch.needsSales === 0 &&
                    branch.completedThisMonth === 0 && (
                      <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                        On track
                      </span>
                    )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default LocationOverview;
